import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { PrimeNG } from 'primeng/config';
import { WorkspacePreferencesService } from './workspace-preferences.service';
import { SidebarService } from './sidebar.service';
import { SessionService } from './session.service';
import { DatePickerLocale, AmsuitePreferences } from '../models';

/**
 * Applica al runtime le preferenze effettive (cascata) lette da Workspace: lingua (ngx-translate +
 * locale PrimeNG) e stato menu. Il fuso resta gestito dal profilo legacy (SessionService.ianaTimezone),
 * tenuto allineato dal dual-write. Non rilancia mai: in caso di errore/campo assente il bootstrap
 * legacy resta il fallback e l'app parte comunque.
 */
@Injectable({ providedIn: 'root' })
export class PreferencesApplierService {
  private _prefs = inject(WorkspacePreferencesService);
  private _translator = inject(TranslateService);
  private _primeConfig = inject(PrimeNG);
  private _sidebar = inject(SidebarService);
  private _session = inject(SessionService);

  constructor() {}

  applyEffective(): Observable<void> {
    // Idrata la cache raw-user (best-effort): così un toggle del menu prima di aprire
    // /preferences non azzera lingua/fuso salvati a user-level (read-modify-write in SidebarService).
    this._prefs.getUser().subscribe({ error: () => {} });
    return this._prefs.getEffective().pipe(
      map(dto => this.applyPrefs(this._prefs.parse(dto?.json))),
      catchError(() => of(void 0))
    );
  }

  /** Applica lingua + stato menu + fuso da un set di preferenze (al bootstrap e dopo un salvataggio). */
  applyPrefs(prefs: AmsuitePreferences): void {
    let lang: 'en' | 'it' | undefined;
    if (prefs?.defaultLanguage) {
      const normalized = prefs.defaultLanguage.toLowerCase();
      lang = normalized === 'en' || normalized === 'en-us' ? 'en' : 'it';
      this._translator.setDefaultLang(lang);
      this._primeConfig.setTranslation(lang === 'en' ? DatePickerLocale.en : DatePickerLocale.it);
    }
    if (prefs?.menuState != null) {
      this._sidebar.setExpand(prefs.menuState);
    }
    // Migrazione runtime: idrata la sessione legacy (DateMomentPipe + usermodel delle librerie)
    // coi valori EFFETTIVI del Workspace. Così il fuso passa dal nuovo al runtime, e lingua/menu
    // restano coerenti col Workspace anche per i consumer che leggono SessionService.
    this._session.patchPreferences({
      defaultLanguage: lang,
      ianaTimezone: prefs?.ianaTimezone,
      menuState: prefs?.menuState
    });
  }
}
